"use client";

import {
  Alert,
  Button,
  ButtonLink,
  Link,
  Select,
} from "@softmax/design-system";
import { breakpoints, spacing } from "@softmax/design-system/tokens.stylex";
import * as stylex from "@stylexjs/stylex";
import { type ReactNode } from "react";
import type { CoworldOption, CreatorStatus } from "./coworld-records";
import { coworldLayout } from "./coworld-layout.styles";
import { CoworldStatus } from "./coworld-status";
import { CoworldVersionNavigation } from "./coworld-version-navigation";
import { CoworldVersionDetails } from "./coworld-version-details";

export function CoworldVersion({
  id,
  visibility,
  publicationDescription,
  uploaded,
  versions,
  hasMoreVersions,
  loadingVersions,
  onMoreVersions,
  onVersionChange,
  checks,
  checkStatus,
  history,
  section,
  runsHref,
  checksHref,
  newRunHref,
  onCopyId,
  copied,
}: Readonly<{
  id: string;
  visibility: string;
  publicationDescription: string;
  uploaded: string;
  versions: readonly CoworldOption[];
  hasMoreVersions: boolean;
  loadingVersions: boolean;
  onMoreVersions(): void;
  onVersionChange(id: string): void;
  checks: ReactNode;
  checkStatus: CreatorStatus;
  history: ReactNode;
  section: "runs" | "checks";
  runsHref: string;
  checksHref: string;
  newRunHref: string;
  onCopyId(): void;
  copied: boolean;
}>) {
  return (
    <div {...stylex.props(coworldLayout.stack)}>
      <div {...stylex.props(coworldLayout.row)}>
        <div {...stylex.props(coworldLayout.inline)}>
          <Select
            aria-label="Coworld version"
            value={id}
            options={versions}
            onValueChange={onVersionChange}
          />
          <CoworldStatus {...checkStatus} />
          {hasMoreVersions && (
            <Button
              size="sm"
              disabled={loadingVersions}
              onClick={onMoreVersions}
            >
              {loadingVersions ? "Loading…" : "Older versions"}
            </Button>
          )}
        </div>
        <ButtonLink href={newRunHref} variant="primary">
          Run privately
        </ButtonLink>
      </div>
      {checkStatus.tone === "danger" && section !== "checks" && (
        <Alert tone="danger">
          Upload checks failed.{" "}
          <Link href={checksHref} focus="native">
            Review checks
          </Link>{" "}
          before running this version.
        </Alert>
      )}
      <div {...stylex.props(styles.workspace)}>
        <div {...stylex.props(styles.table)}>
          <CoworldVersionNavigation
            section={section}
            runsHref={runsHref}
            checksHref={checksHref}
          />
          {section === "checks" ? checks : history}
        </div>
        <CoworldVersionDetails
          id={id}
          visibility={visibility}
          uploaded={uploaded}
          onCopyId={onCopyId}
          copied={copied}
          publicationDescription={publicationDescription}
        />
      </div>
    </div>
  );
}
const styles = stylex.create({
  table: { width: "100%", minWidth: 0 },
  workspace: {
    display: "grid",
    gridTemplateColumns: {
      default: "1fr",
      [breakpoints.wide]: "minmax(0, 1fr) 240px",
    },
    alignItems: "start",
    gap: spacing.space32,
  },
});
